/**
 * @file  open-sdk的公共工具函数 （Public API）
 * @description 此文件中的 export 将对外部项目可见，请勿添加仅用于 SDK 内部的函数。
 * @public
 */
import { assign, find, isNil, map, pick } from '@libs/shared';
import { PLUGIN_EXECUTE_ERROR, SITE_ROUTER, Intl } from '../constants';
import {
    IPlugin,
    PLUGIN_ARGUMENT_FIELD_TYPE,
    PLUGIN_ARGUMENT_VALUE_FROM,
    PLUGIN_FUNCTION_TYPE,
    PluginArgumentField,
    PluginArgumentValue,
    VectorArgumentValue,
    VectorIteratorType
} from '../interfaces';
import pluginIcon from '../assets/plugin-icon.png';
import { getStaticSite } from './site';

/**
 * 获取插件入参的默认值
 *
 * @param parameter
 * @param isVector
 * @param defaultResolverType  插件参数的默认配置项（没有自定义默认值时生效）
 */
export const getResolverType = (
    parameter: PluginArgumentField,
    isVector: boolean,
    defaultResolverType?: PLUGIN_ARGUMENT_VALUE_FROM
) => {
    const { VECTOR, LITERAL } = PLUGIN_ARGUMENT_VALUE_FROM;
    if (isVector) {
        return VECTOR;
    }
    if (!!defaultResolverType && (isNil(parameter.defaultValue) || parameter.defaultValue === '')) {
        // 默认选中：有默认值（包括默认值为0）时选自定义，没有默认值选传来的默认类型
        return defaultResolverType;
    }
    return LITERAL;
};

/**
 * 获取插件入参值
 *
 * @param parameter
 * @param values
 * @param defaultResolverType  插件参数的默认配置项（没有自定义默认值时生效）
 */
export const getPluginArgumentValue = (
    parameter: PluginArgumentField,
    values: PluginArgumentValue[] = [],
    defaultResolverType?: PLUGIN_ARGUMENT_VALUE_FROM
) => {
    const isVector = parameter.fieldType === PLUGIN_ARGUMENT_FIELD_TYPE.VECTOR;
    const defaultParameterValue = {
        id: parameter.id,
        type: parameter.fieldType,
        resolver_type: getResolverType(parameter, isVector, defaultResolverType),
        // vector 不支持设置默认值
        resolver_arg: isVector ? undefined : parameter.defaultValue
    };

    const parameterValue: PluginArgumentValue | undefined = find(values, (v) => {
        return v.id === parameter.id;
    });

    if (isVector) {
        const vectorValue = parameterValue as VectorArgumentValue | undefined;
        assign(defaultParameterValue, {
            // 已有数据，兼容为 map 类型
            iterator_type: vectorValue?.iterator_type || VectorIteratorType.map,
            resolver_ctx: vectorValue?.resolver_ctx
        });

        if (vectorValue) {
            assign(defaultParameterValue, {
                // 初始化子字段默认值
                resolver_arg: map(vectorValue.resolver_arg, (values) => {
                    return getPluginValue(
                        parameter.fieldConf?.fields,
                        values,
                        // 优先使用 vector 指定的 resolver_type
                        parameter.fieldConf?.resolvers?.[parameter.id] || defaultResolverType
                    );
                })
            });
        }
    } else {
        if (parameterValue) {
            assign(defaultParameterValue, pick(parameterValue, ['resolver_type', 'resolver_arg']));
        }
    }

    return defaultParameterValue as PluginArgumentValue;
};

/**
 * 获取插件配置assignments数组
 *
 * @param fields
 * @param values
 * @param defaultResolverType  插件参数的默认配置项（没有自定义默认值时生效）
 */
export const getPluginValue = (
    fields: PluginArgumentField[],
    values: PluginArgumentValue[] = [],
    defaultResolverType?: PLUGIN_ARGUMENT_VALUE_FROM
) => {
    return map(fields, (field) => {
        return getPluginArgumentValue(field, values, defaultResolverType);
    });
};

/**
 * 插件配置-插件已卸载
 */
export const getConfigPluginUninstallTip = () => {
    return Intl.get('plugin.uninstall.tip', {
        url: getStaticSite(SITE_ROUTER.OPEN_PLATFORM_MARKET)
    });
};
/**
 * 插件配置-插件已停用
 */
export const getConfigPluginDisabledTip = () => {
    return Intl.get('plugin.disabled.tip', {
        url: getStaticSite(SITE_ROUTER.OPEN_PLATFORM)
    });
};

/**
 * 插件执行相关报错文案
 *
 * @param {number} errorCode
 * @param {string} errorMsg
 */
export const getPluginExecuteError = (errorCode?: number, errorMsg = '') => {
    switch (errorCode) {
        // 开放平台服务调用失败
        case PLUGIN_EXECUTE_ERROR.OTHER:
            return Intl.get('plugin.execute.fail.other');
        // 未知错误
        case PLUGIN_EXECUTE_ERROR.UNKNOWN:
            return Intl.get('plugin.execute.fail.unknown');
        // 代码有误，请联系开发者
        // 版本过期
        case PLUGIN_EXECUTE_ERROR.VIP_EXPIRE:
            return Intl.get('plugin.execute.fail.vip.expire');
        // 插件已卸载
        case PLUGIN_EXECUTE_ERROR.UNINSTALL:
            return Intl.get('plugin.execute.fail.uninstall');
        // 插件已停用
        case PLUGIN_EXECUTE_ERROR.DISABLED:
            return Intl.get('plugin.execute.fail.disabled');
        // 插件入参不合法
        case PLUGIN_EXECUTE_ERROR.PARAMS:
            return Intl.get('plugin.execute.fail.params');
        // 开发者自定义错误
        case PLUGIN_EXECUTE_ERROR.CUSTOM:
        case PLUGIN_EXECUTE_ERROR.CODE:
            return errorMsg;
        // 云币余额不足
        case PLUGIN_EXECUTE_ERROR.COIN_RECHARGE:
            return Intl.get('plugin.execute.fail.coin.recharge');
        // 未开启云币支付
        case PLUGIN_EXECUTE_ERROR.COIN_REQUIRED:
            return Intl.get('plugin.execute.fail.coin.required');
        // 请检查插件配置
        case PLUGIN_EXECUTE_ERROR.WRONG_CONFIG:
            return Intl.get('plugin.execute.fail.wrong.config');
        // 插件版本过期
        case PLUGIN_EXECUTE_ERROR.EXPIRE:
            return Intl.get('plugin.execute.fail.expire');
        // 函数被删除
        case PLUGIN_EXECUTE_ERROR.FUNCTION_DELETED:
            return Intl.get('plugin.execute.fail.function.deleted');
        case PLUGIN_EXECUTE_ERROR.CONTACT_EXCEED:
            return Intl.get('plugin.execute.fail.contact.exceed');
        case PLUGIN_EXECUTE_ERROR.RUNTIME:
            return Intl.get('plugin.execute.fail.runtime');
        default:
            return errorMsg;
    }
};

/**
 * 获取表单提交时，插件类前端事件执行失败对应的错误文案
 *
 * @param errorCode
 */
export const getFormEventPluginRelyError = (errorCode?: number) => {
    switch (errorCode) {
        case PLUGIN_EXECUTE_ERROR.COIN_RECHARGE:
            return Intl.get('plugin.form.event.fail.coinRecharge');
        case PLUGIN_EXECUTE_ERROR.COIN_REQUIRED:
            return Intl.get('plugin.form.event.fail.coinRequired');
        case PLUGIN_EXECUTE_ERROR.DISABLED:
            return Intl.get('plugin.form.event.fail.disabled');
        case PLUGIN_EXECUTE_ERROR.UNINSTALL:
            return Intl.get('plugin.form.event.fail.uninstall');
        case PLUGIN_EXECUTE_ERROR.VIP_EXPIRE:
            return Intl.get('plugin.form.event.fail.vipExpire');
        case PLUGIN_EXECUTE_ERROR.EXPIRE:
            return Intl.get('plugin.form.event.fail.expire');
        case PLUGIN_EXECUTE_ERROR.FUNCTION_DELETED:
            return Intl.get('plugin.form.event.fail.functionDeleted');
        default:
            return Intl.get('plugin.form.event.fail.other');
    }
};

export const PLUGIN_EXECUTE_ERROR_CODES = Object.values(PLUGIN_EXECUTE_ERROR);

/**
 * 智能助手执行日志-插件相关报错
 *
 * @param {number} errorCode
 * @param errorMsg
 */
export const getAppTriggerLogPluginError = (errorCode: number, errorMsg?: string) => {
    // 插件已卸载
    if (errorCode === PLUGIN_EXECUTE_ERROR.UNINSTALL) {
        return getConfigPluginUninstallTip();
    }

    // 插件已停用
    if (errorCode === PLUGIN_EXECUTE_ERROR.DISABLED) {
        return getConfigPluginDisabledTip();
    }

    return getPluginExecuteError(errorCode, errorMsg);
};

/**
 * 获取插件非表单字段类字段的对应国际化文本
 *
 * @param type
 */
export const getPluginDefaultNameById = (type: string) => {
    switch (type) {
        case 'createTime':
            return Intl.get('field.createTime');
        case 'updateTime':
            return Intl.get('field.updateTime');
        case 'deleteTime':
            return Intl.get('field.delete.time');
        case 'creator':
            return Intl.get('field.creator');
        case 'deleter':
            return Intl.get('field.deleter');
        case 'flowState':
            return Intl.get('field.flowState');
        case 'chargers':
            return Intl.get('field.chargers');
        case 'appId':
            return Intl.get('app.set.id');
        case 'entryId':
            return Intl.get('form.id');
        case '_id':
            return Intl.get('data.id');
        case 'job.begin':
            return Intl.get('timeBegin');
        case 'job.end':
            return Intl.get('timeEnd');
        case 'job.runAt':
            return Intl.get('time.run.at');
        case 'job.prevRunAt':
            return Intl.get('time.prev.run.at');
        case 'job.nextRunAt':
            return Intl.get('time.next.run.at');
        case 'dataInternalLink':
            return Intl.get('data.internal.link');
        default:
            return type;
    }
};

/**
 * 是否为后端函数
 *
 * @param symbol
 */
export function isPluginTriggerSymbol(symbol: string) {
    return /^((([a-z_]+\.)*([a-z_]+))|(func\.[0-9a-fA-F]{24}))$/.test(symbol);
}

/**
 * 是否为前端函数
 *
 * @param symbol
 */
export function isPluginAppletSymbol(symbol: string) {
    return /applet\.[0-9a-fA-F]{24}$/.test(symbol);
}

/**
 * 根据函数 symbol 获取函数类型
 *
 * @param symbol
 */
export function getFunctionType(symbol: string) {
    return isPluginAppletSymbol(symbol)
        ? PLUGIN_FUNCTION_TYPE.FRONT_END
        : PLUGIN_FUNCTION_TYPE.BACK_END;
}

/**
 * 获取插件icon，若没有则使用默认icon
 * @param plugin
 */
export const getPluginIcon = (plugin?: Pick<IPlugin, 'icon'>) => {
    return plugin?.icon || pluginIcon;
};
